import { useContext, useEffect, useState } from 'react';
import { Button, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { datePickerFormat } from 'helpers';
import { EstateResultItem, EstateResultItem_OneViewTradeInput } from 'queries';

import { EstateResultsPageContext } from 'pages/estate-results-page/context/estate-results-page-context';

import {
  CustomResultItemField,
  handleNewCustomFieldButtonClick,
  parseCustomFieldsJson,
  removeCustomField,
  updateCustomField,
} from '../utils/custom-result-item-field';

import NewEssentialData from './new-essential-data';

type Props = {
  estateResultItem?: EstateResultItem;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setEssentialData: Function;
};

const useStyles = makeStyles(() => ({
  button: {
    marginTop: '3px',
    padding: '2px 10px',
  },
  container: {
    paddingTop: '10px',
    textAlign: 'left',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  textField: {
    paddingTop: '10px',
  },
  inputField: {
    textAlign: 'left',
    width: '100%',
  },
  selectContainer: {
    paddingTop: '10px',
    textAlign: 'left',
  },
  title: {
    paddingTop: '10px',
  },
}));

export default function AddTradeItem({ setEssentialData, estateResultItem }: Props) {
  const classes = useStyles();
  const context = useContext(EstateResultsPageContext);

  const [tradeInput, setTradeInput] = useState<EstateResultItem_OneViewTradeInput>({
    type: '',
    accountNumber: 'N/A',
    creditorName: 'N/A',
    creditorType: 'N/A',
    dateOpened: '',
    dateClosed: '',
    lastActiveDate: '',
    status: 'N/A',
    dateReported: '',
    balance: '',
    pastDueAmount: 'N/A',
    actions: '',
    customerName: 'N/A',
    activityDesignatorCode: '',
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [customFields, setCustomFields] = useState<CustomResultItemField[]>([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateOpened, setDateOpened] = useState<Date>();
  const [dateClosed, setDateClosed] = useState<Date>();
  const [dateReported, setDateReported] = useState<Date>();
  const [dateLastActivity, setDateLastActivity] = useState<Date>();

  const fields = [
    'Institution',
    'Account Number',
    'Date Opened',
    'Date Reported',
    'Date Closed',
    'Date of Last Activity',
    'Status',
    'Creditor Name',
    'Creditor Type',
    'Balance',
    'Post Due Amount',
    'Actions',
    'Account Designator Code',
    'Type',
  ];

  const fieldKeys = [
    'customerName',
    'accountNumber',
    'dateOpened',
    'dateReported',
    'dateClosed',
    'lastActiveDate',
    'status',
    'creditorName',
    'creditorType',
    'balance',
    'pastDueAmount',
    'actions',
    'activityDesignatorCode',
    'type',
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const designatorCode: any = {
    B: 'Paid and Closed',
    C: 'Closed',
    D: 'Transfer/Sold/Paid',
    L: 'Lost/Stolen',
    P: 'Paid',
    R: 'Refinanced',
    T: 'Transfer/Sold',
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDateInputChange = (e: any, name: string) => {
    const date = new Date(e);
    const formattedDate = `${date.getMonth() + 1 < 10 ? '0' : ''}${date.getMonth() + 1}/${
      date.getDate() < 10 ? '0' : ''
    }${date.getDate()}/${date.getFullYear()}`;
    tradeInput[name as keyof EstateResultItem_OneViewTradeInput] = formattedDate;
    if (name === 'dateOpened') {
      setDateOpened(e);
    } else if (name === 'dateClosed') {
      setDateClosed(e);
    } else if (name === 'dateReported') {
      setDateReported(e);
    } else {
      setDateLastActivity(e);
    }
    setEssentialData(tradeInput);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (e: any) => {
    const name: string = e.target.name;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value: any = e.target.value;
    const newInput = { ...tradeInput };
    newInput[name as keyof EstateResultItem_OneViewTradeInput] = value;
    setTradeInput(newInput);
    setEssentialData(tradeInput);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectChange = (e: any) => {
    const name: string = e.target.name;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value: any = e.target.value;
    tradeInput[name as keyof EstateResultItem_OneViewTradeInput] = value;
    setSelectedStatus(value);
    setEssentialData(tradeInput);
  };

  const mapExistingEstateReusltItemData = (estateResultItem: EstateResultItem) => {
    const essentialData = JSON.parse(estateResultItem.essentialData!);
    const newInput: EstateResultItem_OneViewTradeInput = {
      type: essentialData.type || '',
      accountNumber: essentialData.accountNumber || 'N/A',
      creditorName: essentialData.creditorName || 'N/A',
      creditorType: essentialData.creditorType || 'N/A',
      dateOpened: essentialData.dateOpened || '',
      dateClosed: essentialData.dateClosed || '',
      lastActiveDate: essentialData.lastActiveDate || '',
      status: essentialData.status || 'N/A',
      dateReported: essentialData.dateReported || '',
      balance: essentialData.balance || '',
      pastDueAmount: essentialData.pastDueAmount || 'N/A',
      actions: essentialData.actions || '',
      customerName: essentialData.customerName || 'N/A',
      activityDesignatorCode: essentialData.activityDesignatorCode || '',
      customFields: essentialData.customFields || {},
    };
    setTradeInput(newInput);
    setSelectedStatus(newInput.status);
    setDateOpened(new Date(newInput.dateOpened));
    setDateClosed(new Date(newInput.dateClosed));
    setDateReported(new Date(newInput.dateReported));
    setDateLastActivity(new Date(newInput.lastActiveDate));
    setCustomFields(parseCustomFieldsJson(essentialData.customFields));
  };

  useEffect(() => {
    const essentialData: any = {};
    customFields.forEach((customField) => {
      essentialData[`customField${customField.id}`] = {
        id: customField.id,
        name: customField.field,
        value: customField.value,
      };
    });
    tradeInput.customFields = essentialData;
    setEssentialData(tradeInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields, tradeInput]);

  useEffect(() => {
    if (estateResultItem) {
      mapExistingEstateReusltItemData(estateResultItem);
    }
  }, [estateResultItem]);

  return (
    <>
      <Typography variant="h5" className={classes.title}>
        {context.isEditingEstateResultItem
          ? 'Edit a Loan or Credit Card'
          : 'Add a Loan or Credit Card'}
      </Typography>
      {fields.map((field, index) => {
        if (field.includes('Date')) {
          return (
            <>
              <Grid className={classes.container}>
                <Grid item xs={12} sm={12}>
                  <DatePicker
                    label={`${field}`}
                    orientation="landscape"
                    value={
                      field.includes('Opened')
                        ? dateOpened
                        : field.includes('Closed')
                          ? dateClosed
                          : field.includes('Activity')
                            ? dateLastActivity
                            : dateReported
                    }
                    minDate={
                      field.includes('Closed')
                        ? dateOpened
                        : field.includes('Activity')
                          ? dateOpened
                          : null
                    }
                    maxDate={
                      field.includes('Opened')
                        ? dateClosed
                        : field.includes('Activity')
                          ? dateClosed
                          : null
                    }
                    onChange={(e) => handleDateInputChange(e, fieldKeys[index])}
                    format={datePickerFormat}
                    className={classes.inputField}
                  />
                </Grid>
              </Grid>
            </>
          );
        } else {
          return (
            <>
              <Grid className={classes.container}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    className={classes.inputField}
                    fullWidth
                    label={field}
                    name={`${fieldKeys[index]}`}
                    value={
                      context.isEditingEstateResultItem
                        ? tradeInput[
                            `${fieldKeys[index] as keyof EstateResultItem_OneViewTradeInput}`
                          ]
                        : null
                    }
                    variant="outlined"
                    onChange={handleInputChange}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
              </Grid>
            </>
          );
        }
      })}
      <Grid className={classes.selectContainer}>
        <Typography variant="caption">Trade Status</Typography>
        <Select
          fullWidth
          name="status"
          variant="outlined"
          labelId="result-item-label"
          value={selectedStatus}
          onChange={handleSelectChange}
        >
          {Object.keys(designatorCode).map((key) => {
            return (
              <MenuItem key={key} value={designatorCode[key as keyof typeof designatorCode]}>
                <Typography noWrap>{designatorCode[key as keyof typeof designatorCode]}</Typography>
              </MenuItem>
            );
          })}
        </Select>
      </Grid>
      <Typography variant="h5" className={classes.title}>
        Custom Fields
      </Typography>
      <Grid className={classes.container}>
        <Grid item xs={12} sm={12}>
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            customFields.map((customField: CustomResultItemField, index: number) => {
              return (
                <NewEssentialData
                  key={index}
                  customField={customField}
                  updateCustomField={(updatedCustomField: CustomResultItemField) => {
                    updateCustomField(updatedCustomField, customFields, setCustomFields);
                  }}
                  handleRemoveClick={() => {
                    removeCustomField(customField, customFields, setCustomFields);
                  }}
                />
              );
            })
          }
        </Grid>
      </Grid>
      <div className={classes.container}>
        {!context.isEditingEstateResultItem && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            className={classes.button}
            onClick={() => {
              handleNewCustomFieldButtonClick(customFields, setCustomFields);
            }}
          >
            + Add new field
          </Button>
        )}
      </div>
    </>
  );
}
